import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import './VipCheckinPage.css';

const CORRECT_PIN = '0731';
const PAGE_SIZE = 10;

const formatDayLabel = (dayValue) => {
  const day = String(dayValue || '').trim().toLowerCase();
  if (day === 'friday') return 'Fri';
  if (day === 'saturday') return 'Sat';
  if (day === 'sunday') return 'Sun';
  return dayValue || '—';
};

const AccessDays = ({ row }) => (
  <div className="vip-checkin-days">
    {row.access_friday && <span className="vip-checkin-day">Fri</span>}
    {row.access_saturday && <span className="vip-checkin-day">Sat</span>}
    {row.access_sunday && <span className="vip-checkin-day">Sun</span>}
    {!row.access_friday && !row.access_saturday && !row.access_sunday && <span className="vip-checkin-day">None</span>}
  </div>
);

const DetailsCell = ({ details }) => {
  if (!details) return <span className="vip-checkin-detail muted">—</span>;
  const seats = (details.seats || []).filter(Boolean);
  const premium_boxes = details.premium_boxes || [];
  const wristbands = details.wristbands || [];
  const festival_passes = details.festival_passes || [];
  return (
    <div className="vip-checkin-details-col">
      {festival_passes.length > 0 && (
        <div className="vip-checkin-detail">
          <span className="vip-checkin-detail-label">Passes:</span>
          {festival_passes.map((fp, i) => (
            <span key={i} className="vip-checkin-detail-val">
              {fp.ticket_type_name} x{fp.quantity}
            </span>
          ))}
        </div>
      )}
      {wristbands.length > 0 && (
        <div className="vip-checkin-detail">
          <span className="vip-checkin-detail-label">Rides:</span>
          {wristbands.map((wb, i) => (
            <span key={i} className="vip-checkin-detail-val">
              {formatDayLabel(wb.day)} x{wb.quantity}
            </span>
          ))}
        </div>
      )}
      {premium_boxes.length > 0 && (
        <div className="vip-checkin-detail">
          <span className="vip-checkin-detail-label">Premium Box:</span>
          <div className="vip-checkin-seats-list">
            {premium_boxes.map((pb, i) => (
              <span key={i} className="vip-checkin-detail-val vip-checkin-detail-val--seat">
                {pb.box_number ? `Premium BOX ${pb.box_number}` : pb.box_name}
              </span>
            ))}
          </div>
        </div>
      )}
      {seats.length > 0 && (
        <div className="vip-checkin-detail">
          <span className="vip-checkin-detail-label">Seats:</span>
          <div className="vip-checkin-seats-list">
            {seats.map((s, i) => (
              <span key={i} className="vip-checkin-detail-val vip-checkin-detail-val--seat">
                {s.event_date ? `${s.event_date} · ` : ''}{s.section_display || s.section} {s.row}-{s.seat_number}
              </span>
            ))}
          </div>
        </div>
      )}
      {festival_passes.length === 0 && wristbands.length === 0 && seats.length === 0 && premium_boxes.length === 0 && (
        <span className="vip-checkin-detail muted">—</span>
      )}
    </div>
  );
};

const SecurityCell = ({ row }) => {
  const sec = row.security_count || 0;
  const armed = row.armed_security_count || 0;
  if (!sec && !armed) return <span className="vip-checkin-stat">—</span>;
  return (
    <div className="vip-checkin-security-col">
      {sec > 0 && <span className="vip-checkin-security">Sec {sec}</span>}
      {armed > 0 && <span className="vip-checkin-security vip-checkin-security--armed">Armed {armed}</span>}
    </div>
  );
};

const VipCheckinPage = () => {
  const [authenticated, setAuthenticated] = useState(() => {
    try {
      return sessionStorage.getItem('vipCheckinAuth') === '1';
    } catch {
      return false;
    }
  });
  const [activeTab, setActiveTab] = useState('vip'); // 'vip' | 'media'
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [savingIds, setSavingIds] = useState(new Set());
  const [saveFlashIds, setSaveFlashIds] = useState(new Set());
  const [drafts, setDrafts] = useState({}); // id -> { obtained, notes }
  const searchRef = useRef(null);
  const topScrollRef = useRef(null);
  const tableWrapRef = useRef(null);

  const [dayFilter, setDayFilter] = useState({ fri: false, sat: false, sun: false });

  const load = async (tab = activeTab) => {
    setLoading(true);
    setError('');
    try {
      const res = tab === 'vip'
        ? await api.getVipCheckinList(CORRECT_PIN)
        : await api.getMediaCheckinList(CORRECT_PIN);
      if (res?.success) {
        const data = res.data || [];
        setRows(data);
        const initialDrafts = {};
        data.forEach((r) => {
          initialDrafts[r.id] = {
            obtained: Number(r.checkin_obtained ?? 0),
            notes: String(r.checkin_notes ?? ''),
          };
        });
        setDrafts(initialDrafts);
      } else {
        setError(res?.error?.message || 'Failed to load list');
      }
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated) {
      load(activeTab);
      if (searchRef.current) searchRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, activeTab]);

  useEffect(() => {
    setPage(1);
  }, [search, dayFilter]);

  useEffect(() => {
    const top = topScrollRef.current;
    const wrap = tableWrapRef.current;
    if (!top || !wrap) return;

    const syncWidth = () => {
      const inner = top.querySelector('.vip-checkin-table-scroll-inner');
      if (inner) {
        inner.style.width = `${wrap.scrollWidth}px`;
      }
    };

    const ro = new ResizeObserver(syncWidth);
    ro.observe(wrap);
    syncWidth();

    const onTopScroll = () => { wrap.scrollLeft = top.scrollLeft; };
    const onWrapScroll = () => { top.scrollLeft = wrap.scrollLeft; };
    top.addEventListener('scroll', onTopScroll);
    wrap.addEventListener('scroll', onWrapScroll);

    return () => {
      ro.disconnect();
      top.removeEventListener('scroll', onTopScroll);
      wrap.removeEventListener('scroll', onWrapScroll);
    };
  }, []);

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pin === CORRECT_PIN) {
      setAuthenticated(true);
      try {
        sessionStorage.setItem('vipCheckinAuth', '1');
      } catch {}
      setPinError('');
    } else {
      setPinError('Invalid PIN. Please try again.');
      setPin('');
    }
  };

  const filtered = useMemo(() => {
    let result = rows;
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter((r) => {
        const hay = [r.first_name, r.last_name].filter(Boolean).join(' ').toLowerCase();
        const emailHay = (r.email || '').toLowerCase();
        return hay.includes(q) || emailHay.includes(q);
      });
    }
    const activeDays = Object.entries(dayFilter).filter(([, v]) => v).map(([k]) => k);
    if (activeDays.length > 0) {
      result = result.filter((r) => activeDays.some((d) => {
        if (d === 'fri') return r.access_friday;
        if (d === 'sat') return r.access_saturday;
        return r.access_sunday;
      }));
    }
    return result;
  }, [rows, search, dayFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const getTotalParty = (plusOnes) => Math.max(1, (Number.isFinite(Number(plusOnes)) ? Number(plusOnes) : 0) + 1);

  const handleDraftChange = (id, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const handleSave = async (row) => {
    const draft = drafts[row.id];
    if (!draft) return;
    setSavingIds((prev) => new Set(prev).add(row.id));
    try {
      const res = activeTab === 'vip'
        ? await api.updateVipCheckin(row.id, CORRECT_PIN, {
            checkin_obtained: Number(draft.obtained),
            checkin_notes: String(draft.notes).trim(),
          })
        : await api.updateMediaCheckin(row.id, CORRECT_PIN, {
            checkin_obtained: Number(draft.obtained),
            checkin_notes: String(draft.notes).trim(),
          });
      if (res?.success) {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, checkin_obtained: Number(draft.obtained), checkin_notes: String(draft.notes).trim() } : r)));
        setSaveFlashIds((prev) => new Set(prev).add(row.id));
        setTimeout(() => {
          setSaveFlashIds((prev) => {
            const next = new Set(prev);
            next.delete(row.id);
            return next;
          });
        }, 1200);
      } else {
        setError(res?.error?.message || 'Save failed');
      }
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  const handleKeyDown = (e, row) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave(row);
    }
  };

  if (!authenticated) {
    return (
      <div className="vip-checkin-gate">
        <div className="vip-checkin-gate-card">
          <div className="vip-checkin-gate-logo">VIP</div>
          <h1 className="vip-checkin-gate-title">VIP Check-In</h1>
          <p className="vip-checkin-gate-subtitle">Enter PIN to access the guest list</p>
          <form onSubmit={handlePinSubmit} className="vip-checkin-gate-form">
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="••••"
              className="vip-checkin-gate-input"
              autoFocus
            />
            {pinError && <div className="vip-checkin-gate-error">{pinError}</div>}
            <button type="submit" className="vip-checkin-gate-button">
              Unlock
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="vip-checkin-page">
      <div className="vip-checkin-header">
        <div className="vip-checkin-header-inner">
          <div className="vip-checkin-brand">
            <span className="vip-checkin-brand-badge">VIP</span>
            <div>
              <h1 className="vip-checkin-brand-title">Check-In</h1>
              <p className="vip-checkin-brand-count">{filtered.length} guests · {rows.length} total</p>
            </div>
          </div>
          <div className="vip-checkin-controls">
            <div className="vip-checkin-tabs">
              <button
                className={`vip-checkin-tab ${activeTab === 'vip' ? 'vip-checkin-tab--active' : ''}`}
                onClick={() => setActiveTab('vip')}
              >
                VIP
              </button>
              <button
                className={`vip-checkin-tab ${activeTab === 'media' ? 'vip-checkin-tab--active' : ''}`}
                onClick={() => setActiveTab('media')}
              >
                Media
              </button>
            </div>
            <div className="vip-checkin-day-filters">
              {['fri','sat','sun'].map((d) => (
                <button
                  key={d}
                  className={`vip-checkin-day-filter ${dayFilter[d] ? 'vip-checkin-day-filter--active' : ''}`}
                  onClick={() => setDayFilter((p) => ({ ...p, [d]: !p[d] }))}
                >
                  {d === 'fri' ? 'Fri' : d === 'sat' ? 'Sat' : 'Sun'}
                </button>
              ))}
            </div>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="vip-checkin-search"
            />
            <button onClick={() => load()} className="vip-checkin-refresh" title="Refresh">
              ↻
            </button>
          </div>
        </div>
      </div>

      <div className="vip-checkin-body">
        {error && (
          <div className="vip-checkin-banner vip-checkin-banner--error">
            {error}
            <button onClick={() => setError('')} className="vip-checkin-banner-close">×</button>
          </div>
        )}

        {loading ? (
          <div className="vip-checkin-loading">
            <div className="vip-checkin-spinner" />
            <span>Loading {activeTab === 'vip' ? 'VIP' : 'Media'} list…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="vip-checkin-empty">
            {search ? 'No matches for your search.' : `No ${activeTab === 'vip' ? 'VIP registrations' : 'media inquiries'} yet.`}
          </div>
        ) : (
          <>
            <div className="vip-checkin-table-scroll" ref={topScrollRef}>
              <div className="vip-checkin-table-scroll-inner" />
            </div>
            <div className="vip-checkin-table-wrap" ref={tableWrapRef}>
              <table className="vip-checkin-table">
                <thead>
                  <tr>
                    <th className="vip-checkin-th">Guest</th>
                    {activeTab === 'vip' && <th className="vip-checkin-th">Artist</th>}
                    {activeTab !== 'vip' && <th className="vip-checkin-th">Type</th>}
                    <th className="vip-checkin-th">Access</th>
                    <th className="vip-checkin-th">Security</th>
                    <th className="vip-checkin-th">Details</th>
                    <th className="vip-checkin-th vip-checkin-th--numeric">Plus Ones</th>
                    <th className="vip-checkin-th vip-checkin-th--numeric">Total</th>
                    <th className="vip-checkin-th vip-checkin-th--numeric">Obtained</th>
                    <th className="vip-checkin-th vip-checkin-th--numeric">Remaining</th>
                    <th className="vip-checkin-th">Notes</th>
                    <th className="vip-checkin-th vip-checkin-th--action"></th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((row) => {
                    const draft = drafts[row.id] || { obtained: 0, notes: '' };
                    const totalParty = getTotalParty(row.plus_ones);
                    const remaining = Math.max(0, totalParty - (Number.isFinite(Number(draft.obtained)) ? Number(draft.obtained) : 0));
                    const isSaving = savingIds.has(row.id);
                    const isFlash = saveFlashIds.has(row.id);
                    const typeLabel = activeTab === 'vip'
                      ? (row.vip_type_display || row.vip_type || '—')
                      : (row.media_type_display || row.media_type || '—');
                    return (
                      <tr key={row.id} className={`vip-checkin-tr ${isFlash ? 'vip-checkin-tr--flash' : ''}`}>
                        <td className="vip-checkin-td">
                          <div className="vip-checkin-guest">
                            <div className="vip-checkin-guest-name">
                              {row.first_name} {row.last_name}
                            </div>
                            {row.organization && (
                              <div className="vip-checkin-guest-org">{row.organization}</div>
                            )}
                          </div>
                        </td>
                        {activeTab !== 'vip' && (
                          <td className="vip-checkin-td">
                            <span className="vip-checkin-badge">{typeLabel}</span>
                          </td>
                        )}
                        {activeTab === 'vip' && (
                          <td className="vip-checkin-td">
                            <span className="vip-checkin-badge">
                              {row.performing_artist
                                ? (row.performing_artist_day ? `Yes · ${row.performing_artist_day.charAt(0).toUpperCase() + row.performing_artist_day.slice(1)}` : 'Yes')
                                : 'No'}
                            </span>
                          </td>
                        )}
                        <td className="vip-checkin-td">
                          <AccessDays row={row} />
                        </td>
                        <td className="vip-checkin-td">
                          <SecurityCell row={row} />
                        </td>
                        <td className="vip-checkin-td">
                          <DetailsCell details={row.checkin_details} />
                        </td>
                        <td className="vip-checkin-td vip-checkin-td--numeric">
                          <span className="vip-checkin-stat">{row.plus_ones ?? 0}</span>
                        </td>
                        <td className="vip-checkin-td vip-checkin-td--numeric">
                          <span className="vip-checkin-stat vip-checkin-stat--total">{totalParty}</span>
                        </td>
                        <td className="vip-checkin-td vip-checkin-td--numeric">
                          <input
                            type="number"
                            min={0}
                            max={totalParty}
                            value={draft.obtained}
                            onChange={(e) => handleDraftChange(row.id, 'obtained', Math.max(0, parseInt(e.target.value, 10) || 0))}
                            onKeyDown={(e) => handleKeyDown(e, row)}
                            className="vip-checkin-input vip-checkin-input--small"
                          />
                        </td>
                        <td className="vip-checkin-td vip-checkin-td--numeric">
                          <span className={`vip-checkin-stat ${remaining === 0 ? 'vip-checkin-stat--zero' : ''}`}>
                            {remaining}
                          </span>
                        </td>
                        <td className="vip-checkin-td">
                          <textarea
                            rows={2}
                            value={draft.notes}
                            onChange={(e) => handleDraftChange(row.id, 'notes', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, row)}
                            placeholder="Add note…"
                            className="vip-checkin-input vip-checkin-input--text"
                          />
                        </td>
                        <td className="vip-checkin-td vip-checkin-td--action">
                          <button
                            onClick={() => handleSave(row)}
                            disabled={isSaving}
                            className={`vip-checkin-save ${isFlash ? 'vip-checkin-save--flash' : ''}`}
                          >
                            {isSaving ? '…' : isFlash ? '✓ Saved' : 'Save'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="vip-checkin-pager">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="vip-checkin-pager-btn"
              >
                Previous
              </button>
              <span className="vip-checkin-pager-info">
                Page <strong>{page}</strong> of <strong>{totalPages}</strong> · {filtered.length} rows
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="vip-checkin-pager-btn"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VipCheckinPage;
